import type { VideoContentObject } from 'models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { ArchiveLineIcon, Flag1LineIcon, type MenuProps, PencilLineIcon } from 'ui';
import { useUpdateStatus } from '../../../../queries';
import { useReportVideo } from './useReportVideo';

export const useActions = ({
  onRequestEdit,
  videoContentObject,
}: {
  onRequestEdit: (opened: true) => void;
  videoContentObject: VideoContentObject;
}) => {
  const { t } = useTranslation();
  const { confirm: reportVideo, contextHolder } = useReportVideo({ videoContentObject });
  const { mutate: updateStatus } = useUpdateStatus();
  const { canArchiveVideo, canEditVideo } = usePermissions();

  return useMemo(() => {
    const actions: MenuProps['items'] = [];

    if (canEditVideo({ video: videoContentObject })) {
      actions.push({
        icon: <PencilLineIcon />,
        key: 'edit',
        label: t('Edit'),
        onClick() {
          onRequestEdit(true);
        },
      });
    }

    actions.push({
      icon: <Flag1LineIcon />,
      key: 'report',
      label: t('Report'),
      onClick() {
        reportVideo();
      },
    });

    if (canArchiveVideo({ video: videoContentObject })) {
      actions.push({
        icon: <ArchiveLineIcon />,
        key: 'archive',
        label: t('Archive'),
        onClick() {
          updateStatus({ id: videoContentObject.video.id, video: { status: 'archived' } });
        },
      });
    }

    return {
      actions,
      contextHolder,
    };
  }, [canEditVideo, videoContentObject, t, canArchiveVideo, contextHolder, onRequestEdit, reportVideo, updateStatus]);
};
